// GETs that cannot hang a page. Each fails after its time limit (the page then shows its error state with a
// "Try again" button, or leaves the item out, never a skeleton for ever), and a non-2xx answer is a failure
// too (production's 502 page is HTML, not data). Used by the home page's feeds and the content pages.

export const PAGE_TIMEOUT_MS = 20000;

// A promise that gives up after `ms` (rejects with Error("timeout")), for a call that has no timeout of its own.
export const withTimeout = (p, ms) =>
  new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error("timeout")), ms);
    Promise.resolve(p).then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e);
      },
    );
  });

// GET `url`; resolves to { body, headers }. as: "json" (default) or "text". init: extra fetch options
// (headers). The time limit covers the answer and reading its body; the request is aborted when it runs out.
export async function getWithTimeout(url, { ms = PAGE_TIMEOUT_MS, as = "json", init = {} } = {}) {
  const ctl = typeof AbortController !== "undefined" ? new AbortController() : null;
  let timer = null;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      if (ctl) ctl.abort();
      reject(new Error("timeout"));
    }, ms);
  });
  try {
    const res = await Promise.race([fetch(url, { ...init, ...(ctl ? { signal: ctl.signal } : {}) }), timeout]);
    if (!res || !res.ok) throw new Error(`HTTP ${res ? res.status : "?"}`);
    const body = await Promise.race([as === "text" ? res.text() : res.json(), timeout]);
    return { body, headers: res.headers };
  } finally {
    clearTimeout(timer);
  }
}

// An API answer's own status ({ status: { statusCode, message } }): a code outside 200-299 is a failure even inside
// an HTTP 200 answer. The Error to throw, or null (no status, as in the site's own data files, is not a failure).
export const bodyStatusError = (json) => {
  const raw = json?.status?.statusCode;
  if (raw === undefined || raw === null) return null;
  const code = Number(raw);
  return Number.isFinite(code) && code >= 200 && code < 300 ? null : new Error(json.status.message || `status ${raw}`);
};

// GET an API route (or a data file) that cannot hang the page and resolve to its body: getWithTimeout's time limit
// and non-2xx check, then bodyStatusError. init: the request's headers. Portfolio pages, Trade Ideas and a screen's
// page use it.
export const getApiJson = async (url, init = {}) => {
  const { body } = await getWithTimeout(url, { init });
  const err = bodyStatusError(body);
  if (err) throw err;
  return body;
};

// GET a JSON file or API route and resolve to its body. Rejects on a network error, a non-2xx answer, bad
// JSON or no answer within `timeoutMs`.
export const fetchJson = (url, timeoutMs = 15000) =>
  getWithTimeout(url, { ms: timeoutMs, init: { headers: { Accept: "application/json" } } }).then((r) => r.body);
